import { useEffect, useRef } from 'react';
import { tinykeys } from 'tinykeys';
import type { HotkeyOptions, HotkeyMap } from './types';

const INPUT_SELECTORS = ['INPUT', 'TEXTAREA', 'SELECT'];

function isInputElement(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) return false;

  if (INPUT_SELECTORS.includes(target.tagName)) return true;

  if ((target as HTMLElement).isContentEditable) return true;

  // TipTap / ProseMirror detection
  if (target.closest('.ProseMirror')) return true;

  return false;
}

export function useHotkeys(keymap: HotkeyMap, options: HotkeyOptions = {}): void {
  const { enabled = true, ignoreInputs = true, event = 'keydown', timeout = 1000 } = options;

  const keymapRef = useRef(keymap);
  keymapRef.current = keymap;

  const optionsRef = useRef({ enabled, ignoreInputs });
  optionsRef.current = { enabled, ignoreInputs };

  useEffect(() => {
    if (!enabled) return;

    const wrappedKeymap: HotkeyMap = {};

    for (const [key, handler] of Object.entries(keymapRef.current)) {
      wrappedKeymap[key] = (event: KeyboardEvent) => {
        if (!optionsRef.current.enabled) return;

        if (optionsRef.current.ignoreInputs && isInputElement(event.target)) {
          return;
        }

        handler(event);
      };
    }

    const unsubscribe = tinykeys(window, wrappedKeymap, {
      event,
      timeout,
    });

    return unsubscribe;
  }, [enabled, event, timeout]);
}
